import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {chatApi} from './api';
import './LineHistoryImport.css';

const VERSION='2026-10-15-line-history-import-v85';
export default function LineHistoryImport({room,onClose,onImported}) {
  const [file,setFile]=useState(null),[preview,setPreview]=useState(null),[result,setResult]=useState(null);
  const [busy,setBusy]=useState(''),[error,setError]=useState('');
  const [senderMap,setSenderMap]=useState({}),[assignments,setAssignments]=useState(null);
  const direct=room.room_type==='DIRECT';
  const payload=useRef(null),lock=useRef(false),alive=useRef(true),dialog=useRef(null),close=useRef(null),closeAction=useRef(onClose);
  closeAction.current=onClose;
  useEffect(()=>{
    alive.current=true;const previous=document.activeElement;close.current?.focus();
    const key=event=>{
      if(event.key==='Escape'&&!lock.current){event.stopPropagation();closeAction.current();}
      if(event.key==='Tab'){
        const nodes=[...dialog.current.querySelectorAll('button,input,select')].filter(node=>!node.matches(':disabled'));
        if(!nodes.length){event.preventDefault();return;}
        const first=nodes[0],last=nodes[nodes.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
      }
    };
    document.addEventListener('keydown',key);
    return()=>{alive.current=false;document.removeEventListener('keydown',key);if(previous?.isConnected)previous.focus();};
  },[]);
  const selectFile=event=>{
    const picked=event.target.files?.[0]||null;
    setFile(null);setPreview(null);setResult(null);setSenderMap({});setAssignments(null);payload.current=null;setError('');
    if(picked&&(!/\.txt$/i.test(picked.name)||picked.size===0||picked.size>5*1024*1024)){setError('LINE에서 내보낸 TXT 파일(최대 5MB)을 선택해 주세요.');return;}
    setFile(picked);
  };
  async function execute(apply=false){
    if(lock.current||!file||apply&&!preview)return;
    lock.current=true;setBusy(apply?'apply':'preview');setError('');
    try{
      if(!apply){
        setPreview(null);
        const text=new TextDecoder('utf-8',{fatal:true}).decode(await file.arrayBuffer());
        payload.current={fileName:file.name,text};
      }
      const {data}=await chatApi.post(`/line-history/rooms/${room.id}/${apply?'apply':'preview'}`,
        {...payload.current,...(direct?{senderMap}:{}),...(apply?{previewToken:preview.previewToken}:{})},{timeout:120000});
      if(!alive.current)return;
      if(apply){setResult(data);setPreview(null);try{await onImported?.(data);}catch{setError('가져오기는 완료됐습니다. 채팅 화면을 새로고침해 주세요.');}}
      else {
        setPreview(data);
        if(data.roomType==='DIRECT'){
          setAssignments({senders:data.senderAssignments,participants:data.participants});
          setSenderMap(Object.fromEntries(data.senderAssignments.map(sender=>[sender.name,sender.employeeId||''])));
        }
      }
    }catch(requestError){
      if(alive.current){
        if(requestError?.response?.status===409)setPreview(null);
        setError(requestError?.response?.data?.message||(/encoding|encoded|decode/i.test(requestError.message||'')?'UTF-8 TXT 파일을 선택해 주세요.':'요청 결과를 확인하지 못했습니다. 미리보기로 다시 확인해 주세요.'));
      }
    }finally{lock.current=false;if(alive.current)setBusy('');}
  }
  const count=value=>Number(value||0).toLocaleString('ko-KR');
  const summary=result||preview;
  return createPortal(<div className="lh84-backdrop" onMouseDown={event=>{if(event.target===event.currentTarget&&!lock.current)onClose();}}>
    <section className="lh84-dialog" ref={dialog} role="dialog" aria-modal="true" aria-labelledby="lh84-title" data-line-history-import-version={VERSION}>
      <header><div><h2 id="lh84-title">과거 LINE 대화 가져오기</h2><p>가져올 채팅방 <strong>{room.title}</strong></p></div>
        <button type="button" ref={close} disabled={Boolean(busy)} onClick={onClose} aria-label="가져오기 창 닫기">×</button></header>
      <div className="lh84-body">
        <p className="lh84-description">{direct?'이 두 사람의 LINE 대화 TXT를 선택해 주세요. 가져온 대화는 상대방에게도 표시됩니다.':'같은 LINE 방에서 내보낸 TXT를 선택해 주세요.'} 원래 날짜로 가져오며, 이미 있는 대화는 건너뜁니다.</p>
        <label className="lh84-file">LINE 대화 TXT<input type="file" accept=".txt,text/plain" onChange={selectFile} disabled={Boolean(busy)||Boolean(result)} /></label>
        {file?<small>{file.name} · {Math.ceil(file.size/1024).toLocaleString('ko-KR')}KB</small>:null}
        {error?<p className="lh84-error" role="alert">{error}</p>:null}
        {busy?<p className="lh84-progress" role="status">{busy==='apply'?'대화를 가져오고 있습니다…':'대화와 중복 여부를 확인하고 있습니다…'}</p>:null}
        {direct&&assignments&&!result?<fieldset className="lh84-sender-map" disabled={Boolean(busy)}><legend>LINE 발신자 연결</legend>
          <p>참여자 {assignments.participants.map(person=>person.name).join(' · ')}. LINE 이름이 다르면 누구인지 선택한 뒤 미리보기를 다시 눌러 주세요.</p>
          {assignments.senders.map(sender=><label key={sender.name}><span>{sender.name} <small>({count(sender.count)}건)</small></span>
            <select aria-label={`${sender.name}의 그룹웨어 참여자`} value={senderMap[sender.name]||''} onChange={event=>{const value=event.target.value;setSenderMap(current=>({...current,[sender.name]:value}));setPreview(null);setError('');}}>
              <option value="">참여자 선택</option>{assignments.participants.map(person=><option key={person.id} value={person.id}>{person.name}</option>)}
            </select></label>)}
          {!preview?<small>발신자 연결을 확인하고 미리보기를 다시 실행해 주세요.</small>:null}
        </fieldset>:null}
        {summary?<>
          <p className="lh84-period">대화 기간 {summary.period[0].slice(0,16)} ~ {summary.period[1].slice(0,16)}</p>
          {result?<p className="lh84-success" role="status"><strong>{count(result.imported)}건을 가져왔습니다.</strong> 채팅방에서 해당 날짜로 이동해 확인할 수 있습니다.</p>:null}
          <dl className="lh84-counts"><div><dt>{result?'추가 완료':'새로 추가'}</dt><dd>{count(result?result.imported:summary.toImport)}건</dd></div>
            <div><dt>이미 있는 대화</dt><dd>{count(summary.duplicates)}건</dd></div>
            {!direct?<div><dt>실시간 연결 이후</dt><dd>{count(summary.afterBindingSkipped)}건 제외</dd></div>:null}
            <div><dt>사진·파일 표시</dt><dd>{count(summary.placeholders)}건</dd></div></dl>
          <p className="lh84-note">{!direct?'실시간 연결 이후 대화는 기존 연동을 사용합니다. ':''}TXT의 사진·파일 표시는 남지만 첨부 원본은 포함되지 않습니다.</p>
          {direct&&summary.mappingRequired?<p className="lh84-note">모든 LINE 발신자를 두 참여자에 연결해야 가져올 수 있습니다.</p>:null}
          {!direct&&summary.unknown?.length?<div className="lh84-note"><strong>직원과 연결되지 않은 발신자</strong><p>{summary.unknown.map(person=>`${person.name} (${count(person.count)}건)`).join(' · ')}</p><p>원본 이름을 표시해 보존합니다.</p></div>:null}
          {!result&&preview?.toImport===0?<p className="lh84-success">추가로 가져올 과거 대화가 없습니다.</p>:null}
          {!result&&summary.samples?.length?<div className="lh84-samples"><strong>가져올 대화 예시</strong>{summary.samples.map((sample,index)=><article key={index}><small>{sample.at.slice(0,16)} · {sample.sender}</small><p>{sample.body}</p></article>)}</div>:null}
        </>:null}
      </div>
      <footer><button type="button" disabled={Boolean(busy)} onClick={onClose}>{result?'완료':'닫기'}</button>
        {!result?<button type="button" disabled={!file||Boolean(busy)} onClick={()=>execute(false)}>미리보기</button>:null}
        {!result&&preview?.toImport>0&&!preview.mappingRequired?<button type="button" className="lh84-primary" disabled={Boolean(busy)} onClick={()=>execute(true)}>{count(preview.toImport)}건 가져오기</button>:null}</footer>
    </section>
  </div>,document.body);
}
